/**
 * SkillConfigPage — 技能资源配置中心
 *
 * Features:
 * - Resource catalog: all skills with their required resources & customization points
 * - Resource binding editor: per-skill resource binding CRUD
 * - Customization editor: 6 dimensions (tone, format, priority, thresholds, templates, rules)
 * - Resolved context preview
 *
 * API:
 *   GET    /skills/resource-catalog
 *   GET    /skills/:skillId/resources
 *   PUT    /skills/:skillId/resources/:key
 *   DELETE /skills/:skillId/resources/:key
 *   GET    /skills/:skillId/customization
 *   PUT    /skills/:skillId/customization
 *   GET    /skills/:skillId/resolved-context
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  Settings,
  Package,
  Loader2,
  Search,
  ChevronRight,
  Save,
  Trash2,
  Eye,
  FolderOpen,
  Database,
  FileJson,
  Palette,
  RefreshCw,
  Plus,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────

interface ResourceSpec {
  key: string;
  type: string;
  required: boolean;
  description?: string;
  defaultLocation?: string;
  defaultFormat?: string;
}

interface CustomizationPoint {
  dimension: string;
  key: string;
  label: string;
  description?: string;
  type: 'string' | 'number' | 'boolean' | 'json';
  defaultValue?: unknown;
}

interface CatalogEntry {
  skillId: string;
  skillName: string;
  version: string;
  riskClass: string;
  requiredResources: ResourceSpec[];
  customizationPoints: CustomizationPoint[];
  defaultResourceStorage: Array<{
    key: string;
    type: string;
    defaultLocation: string;
    defaultFormat: string;
    required: boolean;
  }>;
}

interface ResourceBinding {
  id: string;
  tenant_id: string;
  skill_id: string;
  resource_key: string;
  resource_type: string;
  location: string;
  format: string;
  ref: string | null;
  content: unknown;
  version: number;
  metadata: Record<string, unknown>;
}

interface Customization {
  id: string;
  tone?: Record<string, unknown>;
  format?: Record<string, unknown>;
  priority?: Record<string, unknown>;
  thresholds?: Record<string, unknown>;
  templates?: Record<string, unknown>;
  rules?: Record<string, unknown>;
}

type ViewMode = 'catalog' | 'resources' | 'customization' | 'preview';

// ─── Component ─────────────────────────────────────

export default function SkillConfigPage() {
  const { t } = useTranslation();

  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSkill, setSelectedSkill] = useState<CatalogEntry | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('catalog');

  // Resource binding state
  const [bindings, setBindings] = useState<ResourceBinding[]>([]);
  const [bindingLoading, setBindingLoading] = useState(false);

  // Customization state
  const [customization, setCustomization] = useState<Customization | null>(null);
  const [customizationDraft, setCustomizationDraft] = useState('{}');
  const [saving, setSaving] = useState(false);

  // Preview state
  const [resolvedContext, setResolvedContext] = useState<unknown>(null);

  // ─── Load catalog ────────────────────────────────

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<CatalogEntry[]>('/skills/resource-catalog');
      setCatalog(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  // ─── Load resources for selected skill ───────────

  const loadResources = useCallback(async (skillId: string) => {
    setBindingLoading(true);
    try {
      const data = await api.get<ResourceBinding[]>(`/skills/${skillId}/resources`);
      setBindings(data);
    } catch {
      setBindings([]);
    } finally {
      setBindingLoading(false);
    }
  }, []);

  // ─── Load customization ──────────────────────────

  const loadCustomization = useCallback(async (skillId: string) => {
    try {
      const data = await api.get<Customization>(`/skills/${skillId}/customization`);
      setCustomization(data);
      setCustomizationDraft(JSON.stringify(data, null, 2));
    } catch {
      setCustomization(null);
      setCustomizationDraft('{}');
    }
  }, []);

  // ─── Load resolved context ───────────────────────

  const loadResolvedContext = useCallback(async (skillId: string) => {
    try {
      const data = await api.get<unknown>(`/skills/${skillId}/resolved-context`);
      setResolvedContext(data);
    } catch {
      setResolvedContext(null);
    }
  }, []);

  // ─── Select skill ────────────────────────────────

  const selectSkill = async (entry: CatalogEntry) => {
    setSelectedSkill(entry);
    setViewMode('resources');
    await Promise.all([
      loadResources(entry.skillId),
      loadCustomization(entry.skillId),
    ]);
  };

  // ─── Save resource binding ───────────────────────

  const saveBinding = async (skillId: string, key: string, body: Record<string, unknown>) => {
    setSaving(true);
    try {
      await api.patch(`/skills/${skillId}/resources/${key}`, body);
      await loadResources(skillId);
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete resource binding ─────────────────────

  const deleteBinding = async (skillId: string, key: string) => {
    setSaving(true);
    try {
      await api.delete(`/skills/${skillId}/resources/${key}`);
      await loadResources(skillId);
    } finally {
      setSaving(false);
    }
  };

  // ─── Save customization ──────────────────────────

  const saveCustomization = async () => {
    if (!selectedSkill) return;
    setSaving(true);
    try {
      const parsed = JSON.parse(customizationDraft);
      await api.patch(`/skills/${selectedSkill.skillId}/customization`, parsed);
      await loadCustomization(selectedSkill.skillId);
    } finally {
      setSaving(false);
    }
  };

  // Filter
  const filtered = catalog.filter(
    (c) =>
      c.skillId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.skillName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // ─── Render ──────────────────────────────────────

  return (
    <div className="flex h-full gap-4 p-4">
      {/* ── Left: Skill Catalog List ── */}
      <div className="w-64 shrink-0 flex flex-col gap-2">
        <Card className="flex-1 flex flex-col">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Package className="h-4 w-4" />
              {t('admin.skill.catalog')}
            </CardTitle>
            <div className="relative mt-2">
              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder={t('admin.skill.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-xs"
              />
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto p-2 space-y-0.5">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">{t('admin.skill.noData')}</p>
            ) : (
              filtered.map((entry) => (
                <button
                  key={entry.skillId}
                  onClick={() => selectSkill(entry)}
                  className={cn(
                    'w-full text-left px-2 py-2 rounded text-xs transition-colors',
                    selectedSkill?.skillId === entry.skillId
                      ? 'bg-primary/10 text-primary'
                      : 'hover:bg-muted text-foreground',
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium truncate">{entry.skillName}</span>
                    <Badge variant="outline" className="text-[10px] ml-1 shrink-0">
                      {entry.riskClass}
                    </Badge>
                  </div>
                  <div className="flex gap-1 mt-1">
                    <span className="text-muted-foreground">
                      {entry.requiredResources.length} {t('admin.skill.resources')}
                    </span>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-muted-foreground">
                      {entry.customizationPoints.length} {t('admin.skill.customPoints')}
                    </span>
                  </div>
                </button>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Right: Detail Panel ── */}
      <div className="flex-1 flex flex-col gap-4 min-w-0">
        {!selectedSkill ? (
          <Card className="flex-1 flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <Settings className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">{t('admin.skill.selectHint')}</p>
            </div>
          </Card>
        ) : (
          <>
            {/* Top bar */}
            <Card>
              <CardContent className="py-3 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="font-medium text-sm">{selectedSkill.skillName}</span>
                  <Badge variant="outline" className="text-xs font-mono">{selectedSkill.skillId}</Badge>
                  <Badge variant="outline" className="text-xs">v{selectedSkill.version}</Badge>
                </div>
                <div className="flex gap-1">
                  {(['resources', 'customization', 'preview'] as ViewMode[]).map((mode) => (
                    <Button
                      key={mode}
                      variant={viewMode === mode ? 'default' : 'ghost'}
                      size="sm"
                      onClick={async () => {
                        setViewMode(mode);
                        if (mode === 'preview') await loadResolvedContext(selectedSkill.skillId);
                      }}
                    >
                      {mode === 'resources' && <Database className="h-3.5 w-3.5 mr-1" />}
                      {mode === 'customization' && <Palette className="h-3.5 w-3.5 mr-1" />}
                      {mode === 'preview' && <Eye className="h-3.5 w-3.5 mr-1" />}
                      {mode === 'resources' ? t('admin.skill.resourceBinding') : mode === 'customization' ? t('admin.skill.customization') : t('admin.skill.runtimePreview')}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* View: Resources */}
            {viewMode === 'resources' && (
              <Card className="flex-1 overflow-auto">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FolderOpen className="h-4 w-4" />
                    {t('admin.skill.resourceBinding')} ({selectedSkill.requiredResources.length} {t('admin.skill.declared')} / {bindings.length} {t('admin.skill.bound')})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {bindingLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    selectedSkill.requiredResources.map((spec) => {
                      const bound = bindings.find((b) => b.resource_key === spec.key);
                      return (
                        <div key={spec.key} className="rounded border p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <FileJson className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm font-medium">{spec.key}</span>
                              <Badge variant="outline" className="text-[10px]">{spec.type}</Badge>
                              {spec.required ? (
                                <Badge className="bg-red-500/10 text-red-600 text-[10px]">{t('admin.required')}</Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px]">{t('admin.optional')}</Badge>
                              )}
                            </div>
                            {bound ? (
                              <Badge className="bg-green-500/10 text-green-600 text-[10px]">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                {t('admin.skill.isBound')}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-amber-600 text-[10px]">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                {t('admin.skill.notBound')}
                              </Badge>
                            )}
                          </div>
                          {spec.description && (
                            <p className="text-xs text-muted-foreground">{spec.description}</p>
                          )}
                          {bound && (
                            <div className="rounded bg-muted/50 p-2 text-xs space-y-1">
                              <div className="flex gap-4">
                                <span>Location: <code>{bound.location}</code></span>
                                <span>Format: <code>{bound.format}</code></span>
                                {bound.ref && <span>Ref: <code>{bound.ref}</code></span>}
                                <span>Version: v{bound.version}</span>
                              </div>
                              <div className="flex gap-1 pt-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 text-[10px] px-2 text-red-500"
                                  onClick={() => deleteBinding(selectedSkill.skillId, spec.key)}
                                  disabled={saving}
                                >
                                  <Trash2 className="h-3 w-3 mr-1" />
                                  Unbind
                                </Button>
                              </div>
                            </div>
                          )}
                          {!bound && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>Default: {spec.defaultLocation ?? 'inline_json'} / {spec.defaultFormat ?? 'json'}</span>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                  {selectedSkill.requiredResources.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-8">This skill requires no external resources</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* View: Customization */}
            {viewMode === 'customization' && (
              <Card className="flex-1 flex flex-col">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Palette className="h-4 w-4" />
                    {t('admin.skill.customization')} ({selectedSkill.customizationPoints.length})
                  </CardTitle>
                  <Button size="sm" onClick={saveCustomization} disabled={saving}>
                    {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                    {t('admin.save')}
                  </Button>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col gap-3">
                  {/* Customization points reference */}
                  {selectedSkill.customizationPoints.length > 0 && (
                    <div className="rounded bg-muted/50 p-2 space-y-1">
                      <p className="text-xs font-medium">Configurable dimensions:</p>
                      {selectedSkill.customizationPoints.map((cp) => (
                        <div key={`${cp.dimension}-${cp.key}`} className="text-xs flex gap-2">
                          <Badge variant="outline" className="text-[10px]">{cp.dimension}</Badge>
                          <span className="font-mono">{cp.key}</span>
                          <span className="text-muted-foreground">({cp.type})</span>
                          {cp.description && <span className="text-muted-foreground">— {cp.description}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  <textarea
                    value={customizationDraft}
                    onChange={(e) => setCustomizationDraft(e.target.value)}
                    className="flex-1 w-full resize-none rounded border bg-muted/30 p-3 text-xs font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-primary/50"
                    spellCheck={false}
                  />
                </CardContent>
              </Card>
            )}

            {/* View: Resolved Context Preview */}
            {viewMode === 'preview' && (
              <Card className="flex-1 flex flex-col">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    {t('admin.skill.runtimePreview')}
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadResolvedContext(selectedSkill.skillId)}
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1" />
                    {t('admin.refresh')}
                  </Button>
                </CardHeader>
                <CardContent className="flex-1 overflow-auto">
                  <pre className="text-xs font-mono bg-muted/30 rounded p-3 whitespace-pre-wrap">
                    {resolvedContext ? JSON.stringify(resolvedContext, null, 2) : 'Loading...'}
                  </pre>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
